import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, LogOut, Package, DollarSign, Clock, CheckCircle2,
  Plus, X, Save, RefreshCw, MapPin, Loader2, Pencil, Trash2, Library, Search, Truck, School, Home, Images, Upload, ArrowUp, ArrowDown,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import BookFormDialog from "@/components/BookFormDialog";
import FinancePanel from "@/components/FinancePanel";
import { api, formatCOP } from "@/lib/api";
import { GENRES } from "@/lib/genres";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-5 flex items-center gap-4">
    <span className="grid place-items-center w-12 h-12 rounded-xl" style={{ background: `${color}1a`, color }}>
      <Icon className="w-6 h-6" />
    </span>
    <div>
      <p className="text-sm text-[var(--lc-slate)] font-semibold">{label}</p>
      <p className="font-display font-black text-2xl text-[var(--lc-ink)]">{value}</p>
    </div>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [cities, setCities] = useState([]);
  const [newCity, setNewCity] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingCities, setSavingCities] = useState(false);
  const [books, setBooks] = useState([]);
  const [bookFormOpen, setBookFormOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [deletingBook, setDeletingBook] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [bookSearch, setBookSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [shippingFee, setShippingFee] = useState(0);
  const [savingShipping, setSavingShipping] = useState(false);
  const [deliveryType, setDeliveryType] = useState("colegio");
  const [savingDeliveryType, setSavingDeliveryType] = useState(false);
  const [slides, setSlides] = useState([]);
  const [carouselEnabled, setCarouselEnabled] = useState(false);
  const [savingSlides, setSavingSlides] = useState(false);
  const [uploadingSlide, setUploadingSlide] = useState(false);
  const slideFileRef = useRef(null);

  const loadBooks = async () => {
    const b = await api.get("/books");
    setBooks(b.data);
  };

  const loadAll = async () => {
    const [o, s, c, b, cr] = await Promise.all([
      api.get("/admin/orders"),
      api.get("/admin/stats"),
      api.get("/admin/cities"),
      api.get("/books"),
      api.get("/admin/carousel"),
    ]);
    setOrders(o.data);
    setStats(s.data);
    setCities(c.data.cities || []);
    setShippingFee(c.data.shipping_fee ?? 0);
    setDeliveryType(c.data.delivery_type || "colegio");
    setBooks(b.data);
    setSlides(cr.data.slides || []);
    setCarouselEnabled(!!cr.data.enabled);
  };

  const openNewBook = () => { setEditingBook(null); setBookFormOpen(true); };
  const openEditBook = (book) => { setEditingBook(book); setBookFormOpen(true); };

  const doDelete = async () => {
    if (!deletingBook) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/books/${deletingBook.id}`);
      toast.success("Libro eliminado");
      setDeletingBook(null);
      await loadBooks();
    } catch {
      toast.error("No se pudo eliminar el libro");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then(() => {
        setAuthed(true);
        return loadAll();
      })
      .catch(() => nav("/admin/login"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("Error al cerrar sesión del admin:", err); }
    localStorage.removeItem("lc_admin_token");
    nav("/admin/login");
  };

  const setOrderStatus = async (order, status) => {
    if (status === order.status) return;
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status } : o)));
      const s = await api.get("/admin/stats");
      setStats(s.data);
      const labels = { pendiente: "Pendiente", en_camino: "En camino", entregado: "Entregado" };
      toast.success(`Pedido marcado como "${labels[status]}"`);
    } catch {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const addCity = () => {
    const c = newCity.trim();
    if (!c) return;
    if (cities.some((x) => x.toLowerCase() === c.toLowerCase())) {
      toast.error("Esa ciudad ya está en la lista");
      return;
    }
    setCities((prev) => [...prev, c]);
    setNewCity("");
  };

  const saveCities = async () => {
    setSavingCities(true);
    try {
      const { data } = await api.put("/admin/cities", { cities });
      setCities(data.cities);
      toast.success("Ciudades actualizadas");
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSavingCities(false);
    }
  };

  const saveDeliveryType = async (type) => {
    if (type === deliveryType) return;
    setSavingDeliveryType(true);
    try {
      const { data } = await api.put("/admin/delivery-type", { delivery_type: type });
      setDeliveryType(data.delivery_type);
      toast.success(
        data.delivery_type === "domicilio"
          ? "Entrega a domicilio activada"
          : "Entrega en colegio activada"
      );
    } catch {
      toast.error("No se pudo cambiar el tipo de entrega");
    } finally {
      setSavingDeliveryType(false);
    }
  };

  const saveShipping = async () => {
    setSavingShipping(true);
    try {
      const { data } = await api.put("/admin/shipping", { fee: Number(shippingFee) || 0 });
      setShippingFee(data.shipping_fee);
      toast.success("Valor del envío actualizado");
    } catch {
      toast.error("No se pudo guardar el valor del envío");
    } finally {
      setSavingShipping(false);
    }
  };

  const handleSlideUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (slides.length >= 5) {
      toast.error("Máximo 5 imágenes en el carrusel");
      e.target.value = "";
      return;
    }
    setUploadingSlide(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSlides((prev) => [...prev, { id: `new-${Date.now()}`, image_url: data.url, title: "", subtitle: "", button_text: "", link_type: "none", link_value: "" }]);
      toast.success("Imagen agregada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir la imagen");
    } finally {
      setUploadingSlide(false);
      e.target.value = "";
    }
  };

  const toggleCarousel = async (val) => {
    setCarouselEnabled(val);
    try {
      await api.put("/admin/carousel/toggle", { enabled: val });
      toast.success(val ? "Carrusel activado (reemplaza el banner)" : "Carrusel desactivado (se muestra el banner)");
    } catch {
      setCarouselEnabled(!val);
      toast.error("No se pudo cambiar el estado del carrusel");
    }
  };

  const removeSlide = (id) => setSlides((prev) => prev.filter((s) => s.id !== id));

  const updateSlide = (id, patch) =>
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const moveSlide = (idx, dir) => {
    setSlides((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const saveSlides = async () => {
    setSavingSlides(true);
    try {
      const { data } = await api.put("/admin/carousel", {
        slides: slides.map((s) => ({
          id: s.id?.startsWith("new-") ? null : s.id,
          image_url: s.image_url,
          title: s.title || "",
          subtitle: s.subtitle || "",
          button_text: s.button_text || "",
          link_type: s.link_type || "none",
          link_value: s.link_value || "",
        })),
      });
      setSlides(data.slides || []);
      toast.success("Carrusel actualizado");
    } catch {
      toast.error("No se pudo guardar el carrusel");
    } finally {
      setSavingSlides(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--lc-bg)]">
        <Loader2 className="w-10 h-10 animate-spin text-[var(--lc-primary)]" />
      </div>
    );
  }
  if (!authed) return null;

  const q = orderSearch.trim().toLowerCase();
  const filteredOrders = orders.filter((o) => {
    if (statusFilter !== "todos" && o.status !== statusFilter) return false;
    if (!q) return true;
    const hay = [
      o.id,
      o.id.slice(0, 8),
      o.delivery?.school,
      o.delivery?.city,
      o.delivery?.student,
      ...(o.items || []).map((it) => it.title),
      ...(o.items || []).map((it) => it.author),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });

  const statusCounts = {
    todos: orders.length,
    pendiente: orders.filter((o) => o.status === "pendiente").length,
    en_camino: orders.filter((o) => o.status === "en_camino").length,
    entregado: orders.filter((o) => o.status === "entregado").length,
  };

  const bq = bookSearch.trim().toLowerCase();
  const filteredBooks = !bq
    ? books
    : books.filter((b) => `${b.title} ${b.author}`.toLowerCase().includes(bq));

  const preorderGroups = Object.values(
    orders.reduce((map, o) => {
      if (o.status === "entregado") return map;
      (o.items || []).forEach((it) => {
        if (!it.preorder) return;
        if (!map[it.title]) map[it.title] = { title: it.title, author: it.author, total: 0, refs: [] };
        map[it.title].total += it.quantity;
        map[it.title].refs.push({
          id: o.id,
          school: o.delivery?.school,
          city: o.delivery?.city,
          student: o.delivery?.student,
          qty: it.quantity,
          paid: o.payment_status === "paid",
        });
      });
      return map;
    }, {})
  ).sort((a, b) => b.total - a.total);

  return (
    <div className="min-h-screen bg-[var(--lc-bg)]">
      <header className="bg-white border-b border-[var(--lc-border)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="admin-logo">
            <img
              src="/logo-edibrante.png"
              alt="Edibrante · Leer para crecer · Plan Lector Escolar"
              className="h-11 sm:h-12 w-auto select-none"
              draggable="false"
            />
            <span className="hidden sm:inline font-display font-extrabold text-[var(--lc-slate)] text-sm border-l border-[var(--lc-border)] pl-3">
              Panel de administración
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadAll}
              data-testid="refresh-button"
              className="p-2 rounded-lg hover:bg-[var(--lc-bg)] text-[var(--lc-slate)]"
              title="Actualizar"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="flex items-center gap-2 rounded-full bg-[var(--lc-ink)] text-white px-4 py-2 text-sm font-bold hover:-translate-y-0.5 transition-transform"
            >
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Package} label="Pedidos pagados" value={stats.paid_orders} color="#0EA5E9" />
            <StatCard icon={DollarSign} label="Ingresos" value={formatCOP(stats.revenue)} color="#10B981" />
            <StatCard icon={Clock} label="Por entregar" value={stats.pending} color="#F59E0B" />
            <StatCard icon={CheckCircle2} label="Entregados" value={stats.delivered} color="#8B5CF6" />
          </div>
        )}

        <Tabs defaultValue="orders">
          <TabsList className="mb-6">
            <TabsTrigger value="orders" data-testid="tab-orders">Pedidos</TabsTrigger>
            <TabsTrigger value="preorders" data-testid="tab-preorders">
              Encargos{preorderGroups.length ? ` (${preorderGroups.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="books" data-testid="tab-books">Libros</TabsTrigger>
            <TabsTrigger value="finance" data-testid="tab-finance">Finanzas</TabsTrigger>
            <TabsTrigger value="cities" data-testid="tab-cities">Ciudades de entrega</TabsTrigger>
            <TabsTrigger value="carousel" data-testid="tab-carousel">Carrusel</TabsTrigger>
          </TabsList>

          <TabsContent value="preorders">
            <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-6">
              <h3 className="font-display font-extrabold text-xl text-[var(--lc-ink)] flex items-center gap-2">
                <Package className="w-5 h-5 text-[var(--lc-accent)]" />
                Libros por conseguir (encargos)
              </h3>
              <p className="text-sm text-[var(--lc-slate)] mt-1 mb-5">
                Libros agotados que fueron encargados y aún no se han entregado. Agrupados por título.
              </p>

              {preorderGroups.length === 0 ? (
                <div className="text-center py-12 text-[var(--lc-slate)]" data-testid="preorders-empty">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  No hay encargos pendientes por conseguir.
                </div>
              ) : (
                <div className="space-y-3" data-testid="preorders-list">
                  {preorderGroups.map((g) => (
                    <div key={g.title} className="rounded-2xl border border-[var(--lc-accent)]/30 bg-[var(--lc-accent)]/5 p-4" data-testid={`preorder-group-${g.title}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-display font-bold text-[var(--lc-ink)] leading-tight">{g.title}</p>
                          <p className="text-sm text-[var(--lc-slate)]">{g.author}</p>
                        </div>
                        <span className="shrink-0 rounded-full bg-[var(--lc-accent)] text-white text-sm font-bold px-3 py-1" data-testid={`preorder-qty-${g.title}`}>
                          {g.total} por conseguir
                        </span>
                      </div>
                      <div className="mt-3 border-t border-[var(--lc-accent)]/20 pt-3 space-y-1.5">
                        {g.refs.map((r, i) => (
                          <div key={`${r.id}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[var(--lc-slate)]">
                            <span className="font-mono font-bold text-[var(--lc-ink)]">#{r.id.slice(0, 8).toUpperCase()}</span>
                            <span>·</span>
                            <span>{r.school} ({r.city})</span>
                            <span>·</span>
                            <span>Estudiante: {r.student}</span>
                            <span>·</span>
                            <span className="font-semibold text-[var(--lc-ink)]">x{r.qty}</span>
                            {r.paid ? (
                              <span className="ml-1 rounded-full bg-[var(--lc-success)]/15 text-[var(--lc-success)] text-xs font-bold px-2 py-0.5">Pagado</span>
                            ) : (
                              <span className="ml-1 rounded-full bg-[var(--lc-border)] text-[var(--lc-slate)] text-xs font-bold px-2 py-0.5">Pago pendiente</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="books">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--lc-slate)]" />
                <input
                  value={bookSearch}
                  onChange={(e) => setBookSearch(e.target.value)}
                  placeholder="Buscar libro por título o autor…"
                  data-testid="book-search-input"
                  className="w-full rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none pl-11 pr-4 py-2.5 bg-white"
                />
              </div>
              <button
                onClick={openNewBook}
                data-testid="add-book-button"
                className="flex items-center gap-2 rounded-full bg-[var(--lc-primary)] hover:bg-[var(--lc-primary-hover)] text-white px-5 py-2.5 font-display font-extrabold transition-colors shadow-[0_8px_16px_-4px_rgba(225,70,90,0.45)] shrink-0"
              >
                <Plus className="w-5 h-5" /> Agregar libro
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-books-grid">
              {filteredBooks.length === 0 && (
                <div className="col-span-full text-center py-16 text-[var(--lc-slate)]">
                  <Library className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  {books.length === 0 ? "Aún no hay libros. Agrega el primero." : "Sin resultados para tu búsqueda."}
                </div>
              )}
              {filteredBooks.map((b) => (
                <div key={b.id} className="flex gap-3 bg-white rounded-2xl border border-[var(--lc-border)] p-3" data-testid={`admin-book-${b.id}`}>
                  <img src={b.image_url} alt={b.title} className="w-16 h-24 object-cover object-top rounded-lg bg-[var(--lc-cream)] shrink-0" />
                  <div className="flex flex-col flex-1 min-w-0">
                    <p className="font-display font-bold text-[var(--lc-ink)] leading-tight line-clamp-2">{b.title}</p>
                    <p className="text-sm text-[var(--lc-slate)] line-clamp-1">{b.author}</p>
                    {b.publisher && <p className="text-xs text-[var(--lc-slate)]/80 line-clamp-1">Editorial: {b.publisher}</p>}
                    <p className="font-display font-bold text-[var(--lc-ink)] mt-1">{formatCOP(b.price)}</p>
                    {typeof b.stock === "number" && (
                      <span
                        data-testid={`admin-book-stock-${b.id}`}
                        className={`text-xs font-bold mt-0.5 inline-block ${
                          b.stock <= 0 ? "text-[var(--lc-accent)]" : b.stock <= 5 ? "text-[#B45309]" : "text-[var(--lc-slate)]"
                        }`}
                      >
                        {b.stock <= 0 ? "Agotado" : `Stock: ${b.stock}`}
                      </span>
                    )}
                    <div className="mt-auto pt-2 flex gap-2">
                      <button
                        onClick={() => openEditBook(b)}
                        data-testid={`edit-book-${b.id}`}
                        className="flex items-center gap-1 rounded-lg border border-[var(--lc-border)] px-3 py-1.5 text-sm font-semibold text-[var(--lc-ink)] hover:bg-[var(--lc-cream)]"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                      <button
                        onClick={() => setDeletingBook(b)}
                        data-testid={`delete-book-${b.id}`}
                        className="flex items-center gap-1 rounded-lg border border-[var(--lc-accent)]/30 text-[var(--lc-accent)] px-3 py-1.5 text-sm font-semibold hover:bg-[var(--lc-accent)]/10"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="mb-4 flex flex-wrap gap-2" data-testid="order-status-filters">
              {[
                { key: "todos", label: "Todos" },
                { key: "pendiente", label: "Pendientes" },
                { key: "en_camino", label: "En camino" },
                { key: "entregado", label: "Entregados" },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`rounded-full px-4 py-2 text-sm font-bold border-2 transition-colors ${
                    statusFilter === f.key
                      ? "bg-[var(--lc-primary)] text-white border-[var(--lc-primary)]"
                      : "bg-white text-[var(--lc-ink)] border-[var(--lc-border)] hover:border-[var(--lc-primary)]"
                  }`}
                >
                  {f.label} <span className="opacity-70">({statusCounts[f.key]})</span>
                </button>
              ))}
            </div>
            <div className="mb-4 relative max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--lc-slate)]" />
              <input
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder="Buscar por libro, colegio, ciudad, estudiante o N° de pedido…"
                data-testid="order-search-input"
                className="w-full rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none pl-11 pr-4 py-2.5 bg-white"
              />
            </div>
            <div className="bg-white rounded-2xl border border-[var(--lc-border)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="orders-table">
                  <thead>
                    <tr className="bg-[var(--lc-bg)] text-left text-[var(--lc-slate)] font-bold">
                      <th className="px-4 py-3">Pedido</th>
                      <th className="px-4 py-3">Libros</th>
                      <th className="px-4 py-3">Colegio / Dirección</th>
                      <th className="px-4 py-3">Ciudad</th>
                      <th className="px-4 py-3">Estudiante</th>
                      <th className="px-4 py-3">Total</th>
                      <th className="px-4 py-3">Pago</th>
                      <th className="px-4 py-3">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-[var(--lc-slate)]">
                          {orders.length === 0 ? "Aún no hay pedidos." : "Sin resultados para tu búsqueda."}
                        </td>
                      </tr>
                    )}
                    {filteredOrders.map((o) => {
                      const pre = o.has_preorder || (o.items || []).some((i) => i.preorder);
                      return (
                      <tr
                        key={o.id}
                        className={`border-t border-[var(--lc-border)] align-top ${pre ? "bg-[var(--lc-accent)]/5" : ""}`}
                        data-testid={`order-row-${o.id}`}
                      >
                        <td className="px-4 py-3 font-mono font-bold text-[var(--lc-ink)]">
                          #{o.id.slice(0, 8).toUpperCase()}
                          {pre && (
                            <span data-testid={`preorder-badge-${o.id}`} className="block mt-1 rounded-full bg-[var(--lc-accent)] text-white text-[10px] font-bold px-2 py-0.5 w-fit">
                              Encargo · conseguir
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-[240px]">
                          {o.items.map((it) => (
                            <div key={it.book_id} className={it.preorder ? "text-[var(--lc-accent)] font-semibold" : "text-[var(--lc-ink)]"}>
                              {it.title} <span className={it.preorder ? "text-[var(--lc-accent)]/80" : "text-[var(--lc-slate)]"}>x{it.quantity}</span>
                              {it.preorder && <span className="text-[11px]"> (encargo)</span>}
                            </div>
                          ))}
                        </td>
                        <td className="px-4 py-3">{o.delivery?.school || o.delivery?.address || "—"}</td>
                        <td className="px-4 py-3">{o.delivery?.city ?? "—"}</td>
                        <td className="px-4 py-3">{o.delivery?.student || "—"}</td>
                        <td className="px-4 py-3 font-semibold">{formatCOP(o.amount)}</td>
                        <td className="px-4 py-3">
                          {o.payment_status === "paid" ? (
                            <Badge className="bg-[var(--lc-success)] hover:bg-[var(--lc-success)]">Pagado</Badge>
                          ) : (
                            <Badge variant="secondary">Pendiente</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Select value={o.status} onValueChange={(v) => setOrderStatus(o, v)}>
                            <SelectTrigger
                              data-testid={`status-select-${o.id}`}
                              className={`w-[140px] rounded-full border-2 font-bold text-xs ${
                                o.status === "entregado"
                                  ? "border-[#8B5CF6]/40 text-[#8B5CF6]"
                                  : o.status === "en_camino"
                                  ? "border-[#F59E0B]/50 text-[#B45309]"
                                  : "border-[var(--lc-secondary)]/40 text-[var(--lc-secondary)]"
                              }`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pendiente" data-testid={`status-pendiente-${o.id}`}>Pendiente ⏳</SelectItem>
                              <SelectItem value="en_camino" data-testid={`status-en-camino-${o.id}`}>En camino 🚚</SelectItem>
                              <SelectItem value="entregado" data-testid={`status-entregado-${o.id}`}>Entregado ✓</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="finance">
            <FinancePanel />
          </TabsContent>

          <TabsContent value="cities">
            <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-6 max-w-xl mb-6" data-testid="delivery-type-card">
              <h3 className="font-display font-extrabold text-xl text-[var(--lc-ink)] flex items-center gap-2">
                <Truck className="w-5 h-5 text-[var(--lc-primary)]" />
                Tipo de entrega
              </h3>
              <p className="text-sm text-[var(--lc-slate)] mt-1 mb-5">
                Elige qué dato pedirá la tienda al cliente al pagar. Solo una opción puede estar activa a la vez.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => saveDeliveryType("colegio")}
                  disabled={savingDeliveryType}
                  data-testid="delivery-type-colegio"
                  className={`text-left rounded-2xl border-2 p-4 transition-colors disabled:opacity-60 ${
                    deliveryType === "colegio"
                      ? "border-[var(--lc-primary)] bg-[var(--lc-primary)]/5"
                      : "border-[var(--lc-border)] hover:border-[var(--lc-primary)]/50"
                  }`}
                >
                  <div className="flex items-center gap-2 font-display font-extrabold text-[var(--lc-ink)]">
                    <School className="w-5 h-5 text-[var(--lc-primary)]" /> Entrega en colegio
                    {deliveryType === "colegio" && (
                      <span className="ml-auto rounded-full bg-[var(--lc-primary)] text-white text-[10px] font-bold px-2 py-0.5">
                        Activa
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--lc-slate)] mt-1.5">
                    El cliente ingresa el nombre del colegio y del estudiante.
                  </p>
                </button>

                <button
                  onClick={() => saveDeliveryType("domicilio")}
                  disabled={savingDeliveryType}
                  data-testid="delivery-type-domicilio"
                  className={`text-left rounded-2xl border-2 p-4 transition-colors disabled:opacity-60 ${
                    deliveryType === "domicilio"
                      ? "border-[var(--lc-primary)] bg-[var(--lc-primary)]/5"
                      : "border-[var(--lc-border)] hover:border-[var(--lc-primary)]/50"
                  }`}
                >
                  <div className="flex items-center gap-2 font-display font-extrabold text-[var(--lc-ink)]">
                    <Home className="w-5 h-5 text-[var(--lc-primary)]" /> Entrega a domicilio
                    {deliveryType === "domicilio" && (
                      <span className="ml-auto rounded-full bg-[var(--lc-primary)] text-white text-[10px] font-bold px-2 py-0.5">
                        Activa
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--lc-slate)] mt-1.5">
                    El cliente ingresa su dirección de entrega.
                  </p>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-6 max-w-xl">
              <h3 className="font-display font-extrabold text-xl text-[var(--lc-ink)] flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[var(--lc-primary)]" />
                Ciudades con entrega en colegio
              </h3>
              <p className="text-sm text-[var(--lc-slate)] mt-1 mb-5">
                Solo estas ciudades aparecerán disponibles para los acudientes al pagar.
              </p>

              <div className="flex gap-2 mb-5">
                <input
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCity()}
                  placeholder="Agregar ciudad…"
                  data-testid="new-city-input"
                  className="flex-1 rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none p-3 bg-[var(--lc-bg)]"
                />
                <button
                  onClick={addCity}
                  data-testid="add-city-button"
                  className="rounded-xl bg-[var(--lc-secondary)] text-white px-4 font-bold flex items-center gap-1 hover:-translate-y-0.5 transition-transform"
                >
                  <Plus className="w-4 h-4" /> Agregar
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-6" data-testid="cities-list">
                {cities.map((c) => (
                  <span
                    key={c}
                    className="flex items-center gap-2 rounded-full bg-[var(--lc-primary)]/10 text-[var(--lc-primary)] px-4 py-2 font-bold"
                    data-testid={`city-chip-${c}`}
                  >
                    {c}
                    <button onClick={() => setCities((prev) => prev.filter((x) => x !== c))} data-testid={`remove-city-${c}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
                {cities.length === 0 && (
                  <p className="text-sm text-[var(--lc-slate)]">No hay ciudades configuradas.</p>
                )}
              </div>

              <button
                onClick={saveCities}
                disabled={savingCities}
                data-testid="save-cities-button"
                className="flex items-center gap-2 rounded-full bg-[var(--lc-primary)] text-white px-6 py-3 font-display font-extrabold transition-transform hover:-translate-y-1 disabled:opacity-60 shadow-[0_10px_20px_-4px_rgba(14,165,233,0.45)]"
              >
                {savingCities ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Guardar cambios
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-6 max-w-xl mt-6">
              <h3 className="font-display font-extrabold text-xl text-[var(--lc-ink)] flex items-center gap-2">
                <Truck className="w-5 h-5 text-[var(--lc-primary)]" />
                Valor del envío
              </h3>
              <p className="text-sm text-[var(--lc-slate)] mt-1 mb-5">
                Único para toda la tienda. Se cobra una sola vez por pedido, igual para 1 o varios libros.
              </p>
              <div className="flex gap-2 items-center">
                <span className="font-display font-bold text-lg text-[var(--lc-ink)]">$</span>
                <input
                  type="number"
                  min="0"
                  step="500"
                  value={shippingFee}
                  onChange={(e) => setShippingFee(e.target.value)}
                  placeholder="Ej: 8000"
                  data-testid="shipping-fee-input"
                  className="flex-1 rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none p-3 bg-[var(--lc-bg)]"
                />
                <span className="text-sm text-[var(--lc-slate)] font-semibold">COP</span>
              </div>
              <button
                onClick={saveShipping}
                disabled={savingShipping}
                data-testid="save-shipping-button"
                className="mt-4 flex items-center gap-2 rounded-full bg-[var(--lc-primary)] text-white px-6 py-3 font-display font-extrabold transition-transform hover:-translate-y-1 disabled:opacity-60 shadow-[0_10px_20px_-4px_rgba(14,165,233,0.45)]"
              >
                {savingShipping ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Guardar envío
              </button>
            </div>
          </TabsContent>

          <TabsContent value="carousel">
            <div className="bg-white rounded-2xl border border-[var(--lc-border)] p-6 max-w-3xl" data-testid="carousel-admin">
              <h3 className="font-display font-extrabold text-xl text-[var(--lc-ink)] flex items-center gap-2">
                <Images className="w-5 h-5 text-[var(--lc-primary)]" />
                Carrusel de la página principal
              </h3>
              <p className="text-sm text-[var(--lc-slate)] mt-1 mb-4">
                Imágenes que rotan automáticamente en lugar del banner. Puedes tener entre 1 y 5. La transición se desliza hacia la izquierda.
              </p>

              <div className="flex items-center justify-between gap-4 rounded-xl border-2 border-[var(--lc-border)] bg-[var(--lc-cream)] p-4 mb-4" data-testid="carousel-toggle-row">
                <div>
                  <p className="font-display font-extrabold text-[var(--lc-ink)]">Mostrar el carrusel</p>
                  <p className="text-xs text-[var(--lc-slate)] mt-0.5">
                    {carouselEnabled
                      ? "Activado: el carrusel reemplaza el banner en la página principal."
                      : "Desactivado: se muestra el banner estático normal."}
                  </p>
                </div>
                <Switch checked={carouselEnabled} onCheckedChange={toggleCarousel} data-testid="carousel-enabled-switch" />
              </div>

              <div className="rounded-xl bg-[var(--lc-primary)]/8 border border-[var(--lc-primary)]/25 p-3.5 mb-5 flex items-start gap-2.5" data-testid="carousel-size-hint">
                <Images className="w-4 h-4 text-[var(--lc-primary)] mt-0.5 shrink-0" />
                <p className="text-sm text-[var(--lc-ink)]">
                  <span className="font-bold">Tamaño recomendado: 1536 × 308 px</span> (relación ~5:1, horizontal).
                  Usa imágenes con estas proporciones para que llenen el banner sin recortes ni espacios. Puedes subir en mayor resolución manteniendo la misma proporción (p. ej. 1920 × 385 px).
                </p>
              </div>

              <div className="space-y-4 mb-5">
                {slides.map((s, idx) => (
                  <div key={s.id} className="rounded-xl border-2 border-[var(--lc-border)] p-3 sm:p-4 flex flex-col sm:flex-row gap-4" data-testid={`carousel-admin-slide-${idx}`}>
                    <div className="relative shrink-0 w-full sm:w-56 rounded-lg overflow-hidden">
                      <img src={s.image_url} alt={`Imagen ${idx + 1}`} className="w-full aspect-[1536/308] object-cover" />
                      <div className="absolute top-2 left-2 rounded-full bg-black/55 text-white text-xs font-bold px-2 py-0.5">
                        {idx + 1}
                      </div>
                    </div>

                    <div className="flex-1 space-y-2.5">
                      <div>
                        <label className="text-xs font-bold text-[var(--lc-slate)] mb-1 block">Título (opcional)</label>
                        <input
                          value={s.title || ""}
                          onChange={(e) => updateSlide(s.id, { title: e.target.value })}
                          placeholder="Ej: ¡Nuevos títulos del plan lector!"
                          data-testid={`carousel-title-input-${idx}`}
                          className="w-full rounded-lg border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none p-2.5 bg-[var(--lc-bg)] text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-[var(--lc-slate)] mb-1 block">Subtítulo (opcional)</label>
                        <input
                          value={s.subtitle || ""}
                          onChange={(e) => updateSlide(s.id, { subtitle: e.target.value })}
                          placeholder="Ej: Descuentos en libros del plan lector hasta agotar existencias"
                          data-testid={`carousel-subtitle-input-${idx}`}
                          className="w-full rounded-lg border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none p-2.5 bg-[var(--lc-bg)] text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                        <div>
                          <label className="text-xs font-bold text-[var(--lc-slate)] mb-1 block">Texto del botón (opcional)</label>
                          <input
                            value={s.button_text || ""}
                            onChange={(e) => updateSlide(s.id, { button_text: e.target.value })}
                            placeholder="Ej: Ver ahora"
                            data-testid={`carousel-button-input-${idx}`}
                            className="w-full rounded-lg border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] outline-none p-2.5 bg-[var(--lc-bg)] text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-[var(--lc-slate)] mb-1 block">Al tocar, abrir</label>
                          <Select
                            value={s.link_type || "none"}
                            onValueChange={(v) => updateSlide(s.id, { link_type: v, link_value: "" })}
                          >
                            <SelectTrigger className="rounded-lg border-2 border-[var(--lc-border)] bg-[var(--lc-bg)] text-sm" data-testid={`carousel-linktype-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nada</SelectItem>
                              <SelectItem value="book">Un libro</SelectItem>
                              <SelectItem value="category">Una categoría</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-[var(--lc-slate)] mb-1 block">
                            {s.link_type === "category" ? "Categoría" : s.link_type === "book" ? "Libro" : "Destino"}
                          </label>
                          {s.link_type === "book" ? (
                            <Select value={s.link_value || ""} onValueChange={(v) => updateSlide(s.id, { link_value: v })}>
                              <SelectTrigger className="rounded-lg border-2 border-[var(--lc-border)] bg-[var(--lc-bg)] text-sm" data-testid={`carousel-linkvalue-${idx}`}>
                                <SelectValue placeholder="Selecciona un libro" />
                              </SelectTrigger>
                              <SelectContent>
                                {books.map((b) => (
                                  <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : s.link_type === "category" ? (
                            <Select value={s.link_value || ""} onValueChange={(v) => updateSlide(s.id, { link_value: v })}>
                              <SelectTrigger className="rounded-lg border-2 border-[var(--lc-border)] bg-[var(--lc-bg)] text-sm" data-testid={`carousel-linkvalue-${idx}`}>
                                <SelectValue placeholder="Selecciona categoría" />
                              </SelectTrigger>
                              <SelectContent>
                                {GENRES.map((g) => (
                                  <SelectItem key={g.name} value={g.name}>{g.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <input disabled placeholder="—" className="w-full rounded-lg border-2 border-[var(--lc-border)] p-2.5 bg-[var(--lc-border)]/20 text-sm text-[var(--lc-slate)]" />
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={() => moveSlide(idx, -1)}
                          disabled={idx === 0}
                          data-testid={`carousel-move-up-${idx}`}
                          className="grid place-items-center w-8 h-8 rounded-lg border border-[var(--lc-border)] text-[var(--lc-ink)] hover:bg-[var(--lc-cream)] disabled:opacity-40"
                          title="Mover antes"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveSlide(idx, 1)}
                          disabled={idx === slides.length - 1}
                          data-testid={`carousel-move-down-${idx}`}
                          className="grid place-items-center w-8 h-8 rounded-lg border border-[var(--lc-border)] text-[var(--lc-ink)] hover:bg-[var(--lc-cream)] disabled:opacity-40"
                          title="Mover después"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeSlide(s.id)}
                          data-testid={`carousel-remove-${idx}`}
                          className="ml-auto flex items-center gap-1.5 px-3 h-8 rounded-lg border border-[var(--lc-primary)]/40 text-[var(--lc-primary)] text-sm font-semibold hover:bg-[var(--lc-primary)]/10"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" /> Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {slides.length < 5 && (
                  <button
                    type="button"
                    onClick={() => !uploadingSlide && slideFileRef.current?.click()}
                    data-testid="carousel-add-image"
                    className="w-full rounded-xl border-2 border-dashed border-[var(--lc-border)] bg-[var(--lc-cream)] py-6 grid place-items-center text-[var(--lc-slate)] hover:border-[var(--lc-primary)] hover:text-[var(--lc-primary)] transition-colors"
                  >
                    {uploadingSlide ? (
                      <Loader2 className="w-7 h-7 animate-spin text-[var(--lc-primary)]" />
                    ) : (
                      <span className="flex items-center gap-2">
                        <Upload className="w-6 h-6" />
                        <span className="text-sm font-semibold">Subir imagen</span>
                      </span>
                    )}
                  </button>
                )}
              </div>

              <input
                ref={slideFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleSlideUpload}
                data-testid="carousel-file-input"
              />

              {slides.length === 0 && (
                <p className="text-sm text-[var(--lc-slate)] mb-5">Aún no hay imágenes. Sube al menos una para mostrar el carrusel.</p>
              )}

              <button
                onClick={saveSlides}
                disabled={savingSlides}
                data-testid="save-carousel-button"
                className="flex items-center gap-2 rounded-full bg-[var(--lc-primary)] text-white px-6 py-3 font-display font-extrabold transition-transform hover:-translate-y-1 disabled:opacity-60 shadow-[0_10px_20px_-4px_rgba(14,165,233,0.45)]"
              >
                {savingSlides ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Guardar carrusel
              </button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <BookFormDialog
        open={bookFormOpen}
        onOpenChange={setBookFormOpen}
        book={editingBook}
        onSaved={loadAll}
      />

      <Dialog open={!!deletingBook} onOpenChange={(o) => !o && setDeletingBook(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="delete-book-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-xl text-[var(--lc-ink)]">
              Eliminar libro
            </DialogTitle>
            <DialogDescription className="sr-only">Confirmación para eliminar un libro del catálogo</DialogDescription>
          </DialogHeader>
          <p className="text-[var(--lc-slate)]">
            ¿Seguro que quieres eliminar <strong className="text-[var(--lc-ink)]">{deletingBook?.title}</strong> del catálogo? Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-3 mt-2">
            <button onClick={() => setDeletingBook(null)} className="rounded-full px-5 py-2.5 font-semibold text-[var(--lc-slate)] hover:bg-[var(--lc-cream)]" data-testid="cancel-delete-book">
              Cancelar
            </button>
            <button
              onClick={doDelete}
              disabled={deleting}
              data-testid="confirm-delete-book"
              className="flex items-center gap-2 rounded-full bg-[var(--lc-accent)] text-white px-6 py-2.5 font-display font-extrabold hover:opacity-90 disabled:opacity-60"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
              Eliminar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
